/**
 * Light / dark mode.
 *
 * This replaces the six named palettes the original spec asked for. The app now has one
 * brand palette — jenly.ai's — and the only personal choice is light, dark, or follow the
 * operating system.
 *
 * The preference is stored per person on the server (the `theme` column), so it follows
 * someone between devices rather than living in one browser's storage.
 */

export type ThemeMode = "light" | "dark" | "system";

export const MODES: { id: ThemeMode; label: string; icon: string }[] = [
  { id: "light", label: "Light", icon: "☀" },
  { id: "dark", label: "Dark", icon: "☾" },
  { id: "system", label: "System", icon: "◐" },
];

export const DEFAULT_MODE: ThemeMode = "system";

const VALID = new Set<string>(MODES.map((m) => m.id));

export function isThemeMode(value: string): value is ThemeMode {
  return VALID.has(value);
}

function prefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/** Resolves "system" to whatever the OS currently says. */
export function resolveMode(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") return prefersDark() ? "dark" : "light";
  return mode;
}

/**
 * Applies the mode by setting one attribute; every colour decision lives in CSS.
 *
 * The attribute is always an explicit "light" or "dark" so the stylesheet never has to
 * guess, and the media query in CSS only governs the first paint before this runs.
 */
export function applyMode(mode: string): ThemeMode {
  const resolved: ThemeMode = isThemeMode(mode) ? mode : DEFAULT_MODE;
  document.documentElement.setAttribute("data-theme", resolveMode(resolved));
  return resolved;
}

/**
 * Keeps the page in step with the OS while the person is on "system".
 * Returns an unsubscribe function.
 */
export function watchSystemMode(mode: ThemeMode, onChange: () => void): () => void {
  if (mode !== "system" || !window.matchMedia) return () => {};
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
